import { createSelector } from "reselect"
import {
    selectRegion,
    selectNumAffiliatedCommittees,
    selectNumJointFundraisingCommittees,
    selectNumJointFundraisingRepresentatives,
} from "shared/profiles/political-committee/political-committee-selectors"
import {
    TransactionFilterableListView,
    PoliticalCommitteeFilterableListView,
    NewCampaignFinanceDisbursementsFilterableListView,
    NewCampaignFinanceReceiptsFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"
import paths from "shared/imports/sharedPaths"
import * as helperFunctions from "shared/profiles/tabs/helperFunctions"
import { checkPermission, checkHasAllPermissions } from "shared/imports/permissionFunctions"
import { tabLabels } from "../constants/constants"

export const selectPoliticalCommitteeProfileTabs = (tabs, region) =>
    tabs.filter(
        (tab) =>
            helperFunctions.checkIsAllValidData(tab.objData) && helperFunctions.checkValueExists(tab.region, region),
    )

export const selectTabs = ({ isMobile, scenes }) =>
    createSelector(
        selectRegion,
        selectNumAffiliatedCommittees,
        selectNumJointFundraisingCommittees,
        selectNumJointFundraisingRepresentatives,
        (region, numAffiliatedCommittees, numJointFundraisingCommittees, numJointFundraisingRepresentatives) => {
            const politicalCommitteeTabs = [
                {
                    displayName: "Overview",
                    objData: [],
                    region: [],
                    sceneComponent: scenes && scenes.overview,
                    pathName: paths.overview,
                    defaultProps: {
                        key: "Overview",
                    },
                },
                // Commented out for now, should be readded when Notes are setup for Political Committees
                // {
                //     displayName: 'Notes',
                //     objData: [],
                //     region: [],
                //     sceneComponent: NoteFilterableListView,
                //     pathName: paths.notes,
                //     defaultProps: {
                //         key: 'Notes',
                //         uniqueFlvId: "NoteFilterableListViewPoliticalCommitteeProfile",
                //         // permanentFilterKey: '' TODO
                //     },
                // },
                ...(checkHasAllPermissions([
                    DjangIO.app.models.PermissionType.campaign_finance,
                    DjangIO.app.models.PermissionType.can_access_campaign_finance,
                ])
                    ? [
                          {
                              displayName: "Receipts",
                              objData: [],
                              region: [],
                              sceneComponent: NewCampaignFinanceReceiptsFilterableListView,
                              pathName: paths.receipts,
                              defaultProps: {
                                  key: "Receipts",
                                  uniqueFlvId: "ReceiptsFilterableListViewPoliticalCommitteeProfile",
                                  permanentFilterKey: "receipts_political_committee",
                              },
                              parentTab: tabLabels.CAMPAIGN_FINANCE_TAB,
                          },
                      ]
                    : []),
                ...(checkHasAllPermissions([
                    DjangIO.app.models.PermissionType.campaign_finance,
                    DjangIO.app.models.PermissionType.can_access_campaign_finance,
                ])
                    ? [
                          {
                              displayName: "Disbursements",
                              objData: [],
                              region: [],
                              sceneComponent: NewCampaignFinanceDisbursementsFilterableListView,
                              pathName: paths.disbursements,
                              defaultProps: {
                                  key: "Disbursements",
                                  uniqueFlvId: "DisbursementFilterableListViewPoliticalCommitteeProfile",
                                  permanentFilterKey: "disbursement_political_committee",
                              },
                              parentTab: tabLabels.CAMPAIGN_FINANCE_TAB,
                          },
                      ]
                    : []),
                ...(checkPermission(DjangIO.app.models.PermissionType.qpac_v1)
                    ? [
                          {
                              displayName: "PAC Transactions",
                              objData: [],
                              region: [],
                              sceneComponent: TransactionFilterableListView,
                              pathName: paths.transactions,
                              defaultProps: {
                                  key: "PAC Transactions",
                                  uniqueFlvId: "TransactionFilterableListViewPoliticalCommitteeProfile",
                                  permanentFilterKey: "transaction_political_committee",
                              },
                          },
                      ]
                    : []),
                ...(Boolean(numAffiliatedCommittees)
                    ? [
                          {
                              displayName: "Affiliated Committees",
                              objData: [],
                              region: [],
                              sceneComponent: PoliticalCommitteeFilterableListView,
                              pathName: paths.affiliated_committees,
                              defaultProps: {
                                  key: "affiliated_committees",
                                  uniqueFlvId: "AffiliatedPoliticalCommitteeFilterableListViewProfile",
                                  permanentFilterKey: "affiliated_committees_for_committee",
                              },
                          },
                      ]
                    : []),
                ...(Boolean(numJointFundraisingCommittees)
                    ? [
                          {
                              displayName: "Joint Fundraiser Participants",
                              objData: [],
                              region: [],
                              sceneComponent: PoliticalCommitteeFilterableListView,
                              pathName: paths.joint_fundraiser_participants,
                              defaultProps: {
                                  key: "joint_fundraiser_participants",
                                  uniqueFlvId: "JFCPoliticalCommitteeFilterableListViewProfile",
                                  permanentFilterKey: "get_joint_fundraiser_participants",
                              },
                          },
                      ]
                    : []),
                ...(Boolean(numJointFundraisingRepresentatives)
                    ? [
                          {
                              displayName: "Joint Fundraiser Representatives",
                              objData: [],
                              region: [],
                              sceneComponent: PoliticalCommitteeFilterableListView,
                              pathName: paths.joint_fundraiser_representatives,
                              defaultProps: {
                                  key: "joint_fundraiser_representatives",
                                  uniqueFlvId: "JFCPoliticalCommitteeFilterableListViewProfile",
                                  permanentFilterKey: "get_joint_fundraiser_representatives",
                              },
                          },
                      ]
                    : []),
            ]
            return selectPoliticalCommitteeProfileTabs(
                !isMobile
                    ? politicalCommitteeTabs.filter((tab) => !tab.isMobile)
                    : politicalCommitteeTabs.filter((tab) => !tab.isDesktop),
                region,
            )
        },
    )
